/**
 * Capability Envelope Validator
 *
 * Validates a ScenarioContract against a CapabilityManifest.
 *
 * Two validation levels (after Zod structural parse):
 *   1. Capability  — primitives supported + parameters within constraints
 *   2. Consistency — cross-section invariants (duration, channels, etc.)
 */
import type {
  ScenarioContract,
  CapabilityDeclaration,
  PrimitiveType,
  PrimitiveCapability,
  NumericConstraint,
  EnumConstraint,
  ConstraintValue,
} from '../types/scenario-contract.js'

// ════════════════════════════════════════════════════════════
// Error types
// ════════════════════════════════════════════════════════════

export type ScenarioValidationSeverity = 'error' | 'warning'

export interface ScenarioValidationIssue {
  severity: ScenarioValidationSeverity
  code: string
  path: string
  message: string
  suggestion?: string
}

export interface ScenarioValidationReport {
  valid: boolean
  issues: ScenarioValidationIssue[]
  errors: number
  warnings: number
}

// ════════════════════════════════════════════════════════════
// Helpers
// ════════════════════════════════════════════════════════════

function isNumericConstraint(c: ConstraintValue): c is NumericConstraint {
  return typeof c === 'object' && c !== null && ('min' in c || 'max' in c) && !('enum' in c)
}

function isEnumConstraint(c: ConstraintValue): c is EnumConstraint {
  return typeof c === 'object' && c !== null && 'enum' in c
}

function checkNumericConstraint(
  value: number,
  constraint: NumericConstraint,
  path: string,
  paramName: string,
): ScenarioValidationIssue | null {
  if (constraint.min !== undefined && value < constraint.min) {
    return {
      severity: 'error',
      code: 'PARAM_BELOW_MIN',
      path,
      message: `${paramName} = ${value} is below minimum ${constraint.min}`,
      suggestion: `Set ${paramName} to at least ${constraint.min}`,
    }
  }
  if (constraint.max !== undefined && value > constraint.max) {
    return {
      severity: 'error',
      code: 'PARAM_ABOVE_MAX',
      path,
      message: `${paramName} = ${value} exceeds maximum ${constraint.max}`,
      suggestion: `Set ${paramName} to at most ${constraint.max}`,
    }
  }
  return null
}

function checkEnumConstraint(
  value: string,
  constraint: EnumConstraint,
  path: string,
  paramName: string,
): ScenarioValidationIssue | null {
  if (!constraint.enum.includes(value)) {
    return {
      severity: 'error',
      code: 'PARAM_NOT_IN_ENUM',
      path,
      message: `${paramName} = "${value}" is not in allowed values [${constraint.enum.join(', ')}]`,
      suggestion: `Use one of: ${constraint.enum.join(', ')}`,
    }
  }
  return null
}

function checkParamConstraints(
  params: Record<string, unknown>,
  capability: PrimitiveCapability,
  basePath: string,
): ScenarioValidationIssue[] {
  const issues: ScenarioValidationIssue[] = []

  for (const [paramName, constraint] of Object.entries(capability.constraints)) {
    const value = params[paramName]
    if (value === undefined) continue

    if (isNumericConstraint(constraint) && typeof value === 'number') {
      const issue = checkNumericConstraint(value, constraint, `${basePath}.${paramName}`, paramName)
      if (issue) issues.push(issue)
    } else if (isEnumConstraint(constraint) && typeof value === 'string') {
      const issue = checkEnumConstraint(value, constraint, `${basePath}.${paramName}`, paramName)
      if (issue) issues.push(issue)
    } else if (typeof constraint === 'number') {
      if (typeof value === 'number' && value > constraint) {
        issues.push({
          severity: 'error',
          code: 'PARAM_EXCEEDS_LIMIT',
          path: `${basePath}.${paramName}`,
          message: `${paramName} = ${value} exceeds limit ${constraint}`,
          suggestion: `Reduce ${paramName} to at most ${constraint}`,
        })
      }
    }
  }

  return issues
}

// ════════════════════════════════════════════════════════════
// Capability Validation
// ════════════════════════════════════════════════════════════

export interface CapabilityValidationOptions {
  allowExperimental?: boolean
}

export function validateCapability(
  contract: ScenarioContract,
  capability: CapabilityDeclaration,
  options: CapabilityValidationOptions = {},
): ScenarioValidationIssue[] {
  const { allowExperimental = false } = options
  const issues: ScenarioValidationIssue[] = []

  // Check road primitives
  for (let i = 0; i < contract.stimulus.road.length; i++) {
    const road = contract.stimulus.road[i]
    const path = `stimulus.road[${i}]`
    const cap = capability.supported_primitives[road.type as PrimitiveType]

    if (!cap) {
      issues.push({
        severity: 'error',
        code: 'PRIMITIVE_UNKNOWN',
        path,
        message: `Primitive "${road.type}" is not declared in the capability manifest`,
        suggestion: `Remove this primitive or add it to the capability manifest`,
      })
      continue
    }

    if (cap.status === 'unsupported') {
      issues.push({
        severity: 'error',
        code: 'PRIMITIVE_UNSUPPORTED',
        path,
        message: `Primitive "${road.type}" is not yet supported by the digital twin`,
        suggestion: `Remove this primitive`,
      })
      continue
    }

    if (cap.status === 'experimental' && !allowExperimental) {
      issues.push({
        severity: 'warning',
        code: 'PRIMITIVE_EXPERIMENTAL',
        path,
        message: `Primitive "${road.type}" is experimental — results may be unstable`,
      })
    }

    issues.push(...checkParamConstraints(
      road.params as unknown as Record<string, unknown>,
      cap,
      `${path}.params`,
    ))
  }

  // Check control signals
  const controlMap: [string, PrimitiveType][] = [
    ['speed', 'control.speed'],
    ['steering', 'control.steering'],
    ['brake', 'control.brake'],
    ['throttle', 'control.throttle'],
  ]

  for (const [key, primitiveType] of controlMap) {
    const signal = contract.stimulus.controls[key as keyof typeof contract.stimulus.controls]
    const cap = capability.supported_primitives[primitiveType]
    if (!cap || cap.status === 'unsupported') {
      issues.push({
        severity: 'error',
        code: 'PRIMITIVE_UNSUPPORTED',
        path: `stimulus.controls.${key}`,
        message: `Control primitive "${primitiveType}" is not supported`,
      })
      continue
    }

    const valueConstraint = cap.constraints['value']
    if (valueConstraint && isNumericConstraint(valueConstraint)) {
      for (let i = 0; i < signal.keypoints.length; i++) {
        const issue = checkNumericConstraint(
          signal.keypoints[i].value,
          valueConstraint,
          `stimulus.controls.${key}.keypoints[${i}].value`,
          `${key} value`,
        )
        if (issue) issues.push(issue)
      }
    }
  }

  // Check discrete events
  for (let i = 0; i < contract.events.discrete.length; i++) {
    const evt = contract.events.discrete[i]
    const path = `events.discrete[${i}]`
    const cap = capability.supported_primitives[evt.type as PrimitiveType]

    if (!cap || cap.status === 'unsupported') {
      issues.push({
        severity: 'error',
        code: 'PRIMITIVE_UNSUPPORTED',
        path,
        message: `Event primitive "${evt.type}" is not supported`,
        suggestion: `Remove this event from the scenario`,
      })
      continue
    }

    issues.push(...checkParamConstraints(
      evt.params as unknown as Record<string, unknown>,
      cap,
      `${path}.params`,
    ))
  }

  // Check fault events
  for (let i = 0; i < contract.events.faults.length; i++) {
    const fault = contract.events.faults[i]
    const path = `events.faults[${i}]`
    const cap = capability.supported_primitives[fault.type as PrimitiveType]

    if (!cap || cap.status === 'unsupported') {
      issues.push({
        severity: 'error',
        code: 'PRIMITIVE_UNSUPPORTED',
        path,
        message: `Fault primitive "${fault.type}" is not supported`,
        suggestion: `Remove this fault injection from the scenario`,
      })
      continue
    }

    if (cap.status === 'experimental' && !allowExperimental) {
      issues.push({
        severity: 'warning',
        code: 'PRIMITIVE_EXPERIMENTAL',
        path,
        message: `Fault "${fault.type}" is experimental`,
      })
    }

    issues.push(...checkParamConstraints(
      fault.params as unknown as Record<string, unknown>,
      cap,
      `${path}.params`,
    ))
  }

  // Check observable channels
  for (const channel of contract.observables.channels) {
    if (!capability.supported_observables.includes(channel.id)) {
      issues.push({
        severity: 'warning',
        code: 'OBSERVABLE_UNSUPPORTED',
        path: `observables.channels`,
        message: `Observable channel "${channel.id}" is not in the capability manifest`,
        suggestion: `Remove this channel or add it to supported_observables`,
      })
    }
  }

  return issues
}

// ════════════════════════════════════════════════════════════
// Consistency Validation
// ════════════════════════════════════════════════════════════

export function validateConsistency(contract: ScenarioContract): ScenarioValidationIssue[] {
  const issues: ScenarioValidationIssue[] = []
  const { meta, stimulus, events, observables } = contract

  const duration = meta.duration_s

  // Events must fall within scenario duration
  for (let i = 0; i < events.discrete.length; i++) {
    if (events.discrete[i].time_s > duration) {
      issues.push({
        severity: 'error',
        code: 'EVENT_AFTER_DURATION',
        path: `events.discrete[${i}].time_s`,
        message: `Event at t=${events.discrete[i].time_s}s exceeds scenario duration ${duration}s`,
      })
    }
  }

  for (let i = 0; i < events.faults.length; i++) {
    if (events.faults[i].time_s > duration) {
      issues.push({
        severity: 'error',
        code: 'EVENT_AFTER_DURATION',
        path: `events.faults[${i}].time_s`,
        message: `Fault at t=${events.faults[i].time_s}s exceeds scenario duration ${duration}s`,
      })
    }
  }

  // Control signal keypoints should span the scenario duration
  for (const [key, signal] of Object.entries(stimulus.controls)) {
    const kps = signal.keypoints
    if (kps.length > 0) {
      const lastTime = kps[kps.length - 1].time_s
      if (Math.abs(lastTime - duration) > 0.5) {
        issues.push({
          severity: 'warning',
          code: 'CONTROL_DURATION_MISMATCH',
          path: `stimulus.controls.${key}`,
          message: `Last keypoint at t=${lastTime}s doesn't match scenario duration ${duration}s`,
          suggestion: `Add a keypoint at t=${duration}s or adjust meta.duration_s`,
        })
      }
    }
  }

  // KPI channels must reference declared observable channels
  const channelIds = new Set(observables.channels.map(c => c.id))
  for (let i = 0; i < observables.kpis.length; i++) {
    const kpi = observables.kpis[i]
    if (!channelIds.has(kpi.channel)) {
      issues.push({
        severity: 'error',
        code: 'KPI_CHANNEL_NOT_DECLARED',
        path: `observables.kpis[${i}].channel`,
        message: `KPI "${kpi.name}" references channel "${kpi.channel}" not in observables.channels`,
        suggestion: `Add a channel with id "${kpi.channel}" to observables.channels`,
      })
    }
  }

  // Events should be time-ordered
  for (let i = 1; i < events.discrete.length; i++) {
    if (events.discrete[i].time_s < events.discrete[i - 1].time_s) {
      issues.push({
        severity: 'warning',
        code: 'EVENTS_NOT_ORDERED',
        path: `events.discrete[${i}]`,
        message: `Discrete events not in time order (t=${events.discrete[i].time_s}s after t=${events.discrete[i - 1].time_s}s)`,
        suggestion: `Sort discrete events by time_s`,
      })
    }
  }

  for (let i = 1; i < events.faults.length; i++) {
    if (events.faults[i].time_s < events.faults[i - 1].time_s) {
      issues.push({
        severity: 'warning',
        code: 'FAULTS_NOT_ORDERED',
        path: `events.faults[${i}]`,
        message: `Fault events not in time order`,
        suggestion: `Sort fault events by time_s`,
      })
    }
  }

  return issues
}

// ════════════════════════════════════════════════════════════
// Full Validation Pipeline
// ════════════════════════════════════════════════════════════

export interface FullValidationOptions extends CapabilityValidationOptions {
  skipCapability?: boolean
}

export function validateContract(
  contract: ScenarioContract,
  capability?: CapabilityDeclaration,
  options: FullValidationOptions = {},
): ScenarioValidationReport {
  const issues: ScenarioValidationIssue[] = []

  issues.push(...validateConsistency(contract))

  if (capability && !options.skipCapability) {
    issues.push(...validateCapability(contract, capability, options))
  }

  const errors = issues.filter(i => i.severity === 'error').length
  const warnings = issues.filter(i => i.severity === 'warning').length

  return {
    valid: errors === 0,
    issues,
    errors,
    warnings,
  }
}
